/**
 * 流式解析器。
 *
 * 它面对的是**真实链路**，不是干净的管道：
 *   - 串口上电、波特率切换、USB 重新枚举时会吐出一段垃圾字节；
 *   - 从 BLE 或 WebSocket 收到的分片可能在任何字节处截断；
 *   - 偶发的位错误会让某个"看起来像帧"的位置对不上 CRC。
 *
 * 因此解析器的核心指标不是"能解出帧"，而是**能重新同步**：
 * 校验失败时逐字节前进而不是整帧跳过，并如实统计丢了多少字节、错了多少帧。
 * 这些数字直接进链路诊断面板 —— 一个"永远显示正常"的解析器会让排障无从下手。
 */
import { crc16X25, crcAccumulate } from './crc';
import { compileMessage, messageById } from './descriptor';
import {
  MAVLINK_CORE_HEADER_LENGTH_V1,
  MAVLINK_CORE_HEADER_LENGTH_V2,
  MAVLINK_IFLAG_SIGNED,
  MAVLINK_SIGNATURE_BLOCK_LENGTH,
  MAVLINK_SIGNATURE_HASH_LENGTH,
  MAVLINK_STX_V1,
  MAVLINK_STX_V2,
  type FrameSignature,
  type MavlinkFrame,
} from './frame';
import {
  readTimestamp48,
  type MavlinkSignatureVerifier,
  type SignatureRejection,
} from './signing';

export interface ParserStats {
  /** 成功解出的帧数 */
  frames: number;
  /** CRC 校验失败的帧数。持续增长说明链路有噪声或波特率不对。 */
  crcErrors: number;
  /** 本仓库协议表里没有的消息 ID 的帧数。固件较新时会出现。 */
  unknownMessages: number;
  /** 被丢弃的字节数（重同步时逐字节前进的累计） */
  droppedBytes: number;
  /** 长度字段与消息定义矛盾的帧数 */
  malformedFrames: number;
  /** 载荷被 MAVLink 2 截断过的帧数。这是**正常现象**，但有助于解释日志里的长度差异。 */
  truncatedPayloads: number;
  /** 签名校验失败的帧数 */
  signatureFailures: number;
}

export interface UnknownMessageInfo {
  readonly messageId: number;
  readonly systemId: number;
  readonly componentId: number;
  /**
   * 通过穷举解出的 CRC_EXTRA。
   *
   * 这不是猜的：它是**唯一**能让这一帧 CRC 成立的那个字节值。
   * 换句话说，帧的内部一致性已经被验证过了，只是本地面站的协议表里没有这条消息的定义，
   * 因此解不出字段（只能给原始字节）。
   */
  readonly crcExtra: number;
  /** 原始载荷副本 */
  readonly payload: Uint8Array;
}

export interface SignatureFailureInfo {
  readonly frame: MavlinkFrame;
  readonly rejection: SignatureRejection;
}

export interface ParserOptions {
  /**
   * 配置后会对**带签名位的帧**做校验。
   * 未配置时签名帧照常解出（只是没有"发信人已确认"这个结论）。
   */
  readonly verifier?: MavlinkSignatureVerifier;
  readonly now?: () => number;
  readonly onFrame?: (frame: MavlinkFrame) => void;
  readonly onUnknownMessage?: (info: UnknownMessageInfo) => void;
  readonly onSignatureFailure?: (info: SignatureFailureInfo) => void;
}

const INITIAL_BUFFER_SIZE = 8192;

/**
 * 穷举出能让这一帧 CRC 成立的那个 CRC_EXTRA。
 *
 * 协议表里没有的消息算不出 CRC_EXTRA，于是"帧对不对"这个问题失去了判据。
 * 但反过来想：CRC_EXTRA 只有 256 种可能，把 256 个候选代进去，
 * **恰好有一个**能让 16 位 CRC 对上，那么这个候选就是真正的 CRC_EXTRA ——
 * 帧的内部一致性因此仍然被验证过，而不是"只能无条件接受"。
 *
 * 两个作用：
 *   1. 对端固件较新、发来本表未收录的消息时，能把它与链路噪声区分开；
 *   2. 帧起点错位（垃圾里凑巧有个 0xFD）时，几乎必然找不到候选，
 *      于是可以放心地逐字节重同步，而不是按错误的长度跳过去把真数据吞掉。
 *
 * 代价是每帧最多 256 次表查，而这只发生在"本表未收录的消息"上。
 * 这里刻意**不缓存**解出的结果：那个 0.4% 的误判一旦被缓存下来，
 * 之后同 ID 的合法帧都会被用错误的 CRC_EXTRA 校验而失败。
 */function findCrcExtra(
  buffer: Uint8Array,
  crcStart: number,
  crcEnd: number,
  expected: number,
): number | null {
  const base = crc16X25(buffer, crcStart, crcEnd);
  /*
   * `crcAccumulate(base, ·)` 在字节域上是**单射**（查表 + 异或都是双射），
   * 因此最多只可能有一个候选成立：找到即证明，找不到即否定。不存在"歧义"这种中间状态。
   */
  for (let candidate = 0; candidate < 256; candidate += 1) {
    if (crcAccumulate(base, candidate) === expected) return candidate;
  }
  return null;
}

export class MavlinkParser {
  readonly stats: ParserStats = {
    frames: 0,
    crcErrors: 0,
    unknownMessages: 0,
    droppedBytes: 0,
    malformedFrames: 0,
    truncatedPayloads: 0,
    signatureFailures: 0,
  };

  /** 见过的未知消息 ID。给"固件是不是比协议表新"这个问题提供直接证据。 */
  readonly unknownMessageIds = new Set<number>();

  private buffer = new Uint8Array(INITIAL_BUFFER_SIZE);
  private length = 0;
  private readonly options: ParserOptions;
  /**
   * 正在 drain 中。**这是本文件最重要的一行状态**：
   * 帧回调里同步写回链路（模拟机、回环通道，以及任何"收到帧就回一条"的实现）
   * 会让 `push` 在回调里被重入。没有这个标志时，内层 drain 会带着自己的局部游标
   * 从缓冲区开头重新解析，把已经交付过的帧**再交付一遍**；
   * 更糟的是内层的尾部压缩会挪动外层的游标所指向的数据，于是帧被静默吞掉。
   */
  private draining = false;
  /**
   * 缓冲区代次。`reset()` 会作废整个缓冲区，而 reset 可能发生在帧回调里
   * （典型场景：处理某帧时发现链路已重建）。代次变化时中途放弃解析是唯一正确的做法 ——
   * 否则外层游标会落到新数据上，从中间开始切帧。
   */
  private generation = 0;

  constructor(options: ParserOptions = {}) {
    this.options = options;
  }

  /** 写入一段字节并尽可能多地解出帧。可以随时以任意边界切分调用。 */
  push(chunk: Uint8Array): void {
    if (chunk.length === 0) return;
    this.ensureCapacity(this.length + chunk.length);
    this.buffer.set(chunk, this.length);
    this.length += chunk.length;

    // 已经在 drain 中：字节已经追加到缓冲区尾部，外层循环会读到它们。
    // 这里若再递归下去就会重复交付（见 `draining` 的注释）。
    if (this.draining) return;

    this.draining = true;
    try {
      this.drain();
    } finally {
      this.draining = false;
    }
  }

  /**
   * 丢弃缓冲区里未成帧的残余数据。
   * 链路重建时必须调用：旧链路的半截帧与新链路的数据拼在一起会产生随机"帧"。
   */
  reset(): void {
    this.length = 0;
    this.generation += 1;
  }

  private ensureCapacity(needed: number): void {
    if (this.buffer.length >= needed) return;
    let size = this.buffer.length;
    while (size < needed) size *= 2;
    const grown = new Uint8Array(size);
    grown.set(this.buffer.subarray(0, this.length));
    this.buffer = grown;
  }

  private drain(): void {
    const generation = this.generation;
    const now = this.options.now ?? (() => Date.now());
    let position = 0;

    while (position < this.length) {
      // 帧回调里可能发生 `reset()`（链路重建）。此刻缓冲区已经作废，
      // 继续用外层游标解析只会从新数据中间开始切帧。
      if (this.generation !== generation) return;

      /*
       * 每轮重新取缓冲区引用，**不要**在循环外捕获：
       * 帧回调里的同步写入可能触发 `ensureCapacity` 换掉整个数组，
       * 捕获在外的引用会指向旧数组，于是新追加的字节永远读不到。
       */
      const buffer = this.buffer;
      const stx = buffer[position];
      if (stx !== MAVLINK_STX_V1 && stx !== MAVLINK_STX_V2) {
        position += 1;
        this.stats.droppedBytes += 1;
        continue;
      }

      const version: 1 | 2 = stx === MAVLINK_STX_V2 ? 2 : 1;
      const headerLength =
        version === 2 ? MAVLINK_CORE_HEADER_LENGTH_V2 : MAVLINK_CORE_HEADER_LENGTH_V1;

      // 头部还没收全：停在这里等下一次 push
      if (position + 1 + headerLength > this.length) break;

      const payloadLength = buffer[position + 1] ?? 0;

      const incompatFlags = version === 2 ? (buffer[position + 2] ?? 0) : 0;
      const isSigned = version === 2 && (incompatFlags & MAVLINK_IFLAG_SIGNED) !== 0;
      const signatureLength = isSigned ? MAVLINK_SIGNATURE_BLOCK_LENGTH : 0;
      const frameLength = 1 + headerLength + payloadLength + 2 + signatureLength;

      if (position + frameLength > this.length) break;

      const messageId =
        version === 2
          ? (buffer[position + 7] ?? 0) |
            ((buffer[position + 8] ?? 0) << 8) |
            ((buffer[position + 9] ?? 0) << 16)
          : (buffer[position + 5] ?? 0);

      const systemId = buffer[position + (version === 2 ? 5 : 3)] ?? 0;
      const componentId = buffer[position + (version === 2 ? 6 : 4)] ?? 0;
      const sequence = buffer[position + (version === 2 ? 4 : 2)] ?? 0;

      const definition = messageById(messageId);

      const crcStart = position + 1;
      const crcEnd = crcStart + headerLength + payloadLength;
      const expected = (buffer[crcEnd] ?? 0) | ((buffer[crcEnd + 1] ?? 0) << 8);

      if (definition === undefined) {
        /*
         * 协议表里没有这条消息。先判断"这是不是一帧"：
         * 穷举 CRC_EXTRA 若恰好有一个候选成立，说明帧的内部一致性没问题，
         * 只是本表没有它的定义 —— 这几乎只可能发生在"固件比本地面站新"的情况。
         * 认出来之后按帧长前进，保持对齐（不能因为不认识就把后续真数据切碎）。
         */
        const learned = findCrcExtra(buffer, crcStart, crcEnd, expected);
        if (learned === null) {
          // 更可能是"帧起点看错了"，逐字节重同步
          this.stats.crcErrors += 1;
          this.stats.droppedBytes += 1;
          position += 1;
          continue;
        }

        const payloadStart = crcStart + headerLength;
        this.stats.unknownMessages += 1;
        this.unknownMessageIds.add(messageId);
        this.options.onUnknownMessage?.({
          messageId,
          systemId,
          componentId,
          crcExtra: learned,
          payload: buffer.slice(payloadStart, payloadStart + payloadLength),
        });
        position += frameLength;
        continue;
      }

      let crc = crc16X25(buffer, crcStart, crcEnd);
      crc = crcAccumulate(crc, definition.crcExtra);

      if (crc !== expected) {
        // 逐字节前进：错误的"帧起点"可能就在接下来一两个字节处
        this.stats.crcErrors += 1;
        this.stats.droppedBytes += 1;
        position += 1;
        continue;
      }

      const compiled = compileMessage(definition);
      if (payloadLength > compiled.wireLength) {
        this.stats.malformedFrames += 1;
        position += frameLength;
        continue;
      }

      const receivedAt = now();

      /*
       * 零填充到完整长度。MAVLink 2 会截掉尾部的零字节，收到短载荷是**正常**的；
       * 补齐之后，所有按字段偏移读取的代码都不必再关心帧的版本与截断。
       */
      const payload = new Uint8Array(compiled.wireLength);
      const payloadStart = crcStart + headerLength;
      payload.set(buffer.subarray(payloadStart, payloadStart + payloadLength));
      if (payloadLength < compiled.wireLength) this.stats.truncatedPayloads += 1;

      let signature: FrameSignature | undefined;
      let rejection: SignatureRejection | null = null;
      if (isSigned) {
        const signatureStart = crcEnd + 2;
        const hash = new Uint8Array(MAVLINK_SIGNATURE_HASH_LENGTH);
        // 必须拷贝：缓冲区在 drain 结束后会被压缩，视图会失效
        hash.set(buffer.subarray(signatureStart + 7, signatureStart + 13));
        const timestamp = readTimestamp48(buffer, signatureStart + 1);
        const linkId = buffer[signatureStart] ?? 0;

        const verifier = this.options.verifier;
        rejection =
          verifier === undefined
            ? null
            : verifier.verify(buffer.subarray(1 + position, crcEnd + 2), {
                linkId,
                timestamp,
                hash,
                verified: false,
              }, now());
        signature = { linkId, timestamp, hash, verified: rejection === null && verifier?.enabled === true };
      }

      const frame: MavlinkFrame = {
        version,
        sequence,
        systemId,
        componentId,
        messageId,
        payload,
        payloadLength,
        incompatFlags,
        compatFlags: version === 2 ? (buffer[position + 3] ?? 0) : 0,
        receivedAt,
        ...(signature === undefined ? {} : { signature }),
      };

      if (rejection !== null) {
        this.stats.signatureFailures += 1;
        this.options.onSignatureFailure?.({ frame, rejection });
      }

      this.stats.frames += 1;
      this.options.onFrame?.(frame);
      position += frameLength;
    }

    if (position > 0) {
      // 把未消费的尾部搬到缓冲区开头，避免无界增长。
      // 取 max(0, ...) 而不是直接相减：`reset()` 可能已经在回调里把 length 归零，
      // 相减会得到负数并让 length 变成一个非法值，之后每次 push 都会抛错。
      const remaining = Math.max(0, this.length - position);
      if (remaining > 0) this.buffer.copyWithin(0, position, this.length);
      this.length = remaining;
    }
  }
}

/** 一次性把一段字节全部解出。用于回放、测试与离线分析，不适用于长连接。 */
export function parseFrames(bytes: Uint8Array, options: ParserOptions = {}): MavlinkFrame[] {
  const frames: MavlinkFrame[] = [];
  const parser = new MavlinkParser({ ...options, onFrame: (frame) => frames.push(frame) });
  parser.push(bytes);
  return frames;
}
